import { useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faMagnifyingGlass,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import swal from "sweetalert";
import { APISD } from "../../../../../../config/apisd";
import { APIMS } from "../../../../../../config/apims";
import ToastError from "../../../../../NotificationToast/ToastError";
import ToastSuccess from "../../../../../NotificationToast/ToastSuccess";

const MODE_CONFIG = {
  semester: { title: "Atur Rombel / Lanjut Semester", subtitle: "Lanjutkan siswa ke semester berikutnya pada tingkat yang sama." },
  promotion: { title: "Atur Rombel / Naik Kelas", subtitle: "Naikkan siswa ke tahun ajaran dan tingkat berikutnya." },
};
const semesterOrder = (item) => {
  const year = Number(String(item?.name_year || "").match(/\d{4}/)?.[0] || 0);
  return year * 2 + Number(item?.semester || 0);
};
const academicYearId = (item, years) => item?.ysb_academic_year_id || years.find((year) => year.name_year === item?.name_year)?.id;

const normalize = (value) => String(value || "").toLowerCase().trim();
const studentNameCollator = new Intl.Collator("id", { sensitivity: "base", numeric: true });
// Urutan jenjang sekolah: SD, SMP, lalu SMA (termasuk SDIA/SMPIA/SMAIA).
const schoolOrder = (school) => {
  const label = String(school?.school_name || school?.school_code || "").toUpperCase().trim();
  if (/(^|[^A-Z])SD/.test(label)) return 0;
  if (/(^|[^A-Z])SMP/.test(label)) return 1;
  if (/(^|[^A-Z])SMA/.test(label)) return 2;
  return 3;
};
const compareSchools = (a, b) => schoolOrder(a) - schoolOrder(b) ||
  studentNameCollator.compare(a.school_name || "", b.school_name || "");
const sortClassesByName = (classes) => [...classes].sort((a, b) =>
  studentNameCollator.compare(String(a.name_kelas || ""), String(b.name_kelas || ""))
);
const sortStudentsByName = (students) => [...students].sort((a, b) =>
  studentNameCollator.compare(a.name || "", b.name || "") ||
  studentNameCollator.compare(String(a.nisn || ""), String(b.nisn || ""))
);
const formatClassName = (nameLevel, nameClass) =>
  nameLevel == null || String(nameLevel).trim() === ""
    ? `Kelas ${nameClass}`
    : `Kelas ${nameLevel} - ${nameClass}`;

export default function PromotionModal(props) {
  const semesters = useMemo(() => [...(props.semesters || [])].sort((a, b) => semesterOrder(a) - semesterOrder(b)), [props.semesters]);
  const destinationTerm = semesters.find((item) => Number(item.status) === 1);
  const mode = Number(destinationTerm?.semester) === 2 ? "semester" : "promotion";
  const config = MODE_CONFIG[mode];
  const activeYear = (props.academicYears || []).find((item) => Number(item.status) === 1);
  const previousTerm = semesters.filter((item) => semesterOrder(item) < semesterOrder(destinationTerm)).at(-1);
  const [sourceStudents, setSourceStudents] = useState([]);
  const [placements, setPlacements] = useState({});
  const placementsRef = useRef(placements);
  placementsRef.current = placements;
  const [savedPlacements, setSavedPlacements] = useState({});
  const [selectedSource, setSelectedSource] = useState([]);
  const [selectedDestination, setSelectedDestination] = useState([]);
  const [sourceSearch, setSourceSearch] = useState("");
  const [destinationSearch, setDestinationSearch] = useState("");
  const [sourceSemester, setSourceSemester] = useState(previousTerm?.id || "");
  const [destinationSemester, setDestinationSemester] = useState(destinationTerm?.id || "");
  const [sourceClass, setSourceClass] = useState("");
  const [destinationClass, setDestinationClass] = useState(props.filters?.ysb_kelas_id || "");
  const [modalBranch, setModalBranch] = useState(props.filters?.ysb_branch_id || "");
  const [modalSchool, setModalSchool] = useState(props.filters?.ysb_school_id || "");
  const [modalSchools, setModalSchools] = useState(props.schools || []);
  const [modalLevels, setModalLevels] = useState(props.levels || []);
  const [allClasses, setAllClasses] = useState(props.classes || []);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [loadingSource, setLoadingSource] = useState(false);
  const [saving, setSaving] = useState(false);
  const userId = (localStorage.getItem("id_admin") || "").replace(/[\[\]"\s]/g, "");
  const headers = { headers: { Authorization: localStorage.getItem("token"), "Content-Type": "application/json" } };
  const [destinationLevelId, setDestinationLevelId] = useState(props.filters?.ysb_grade_level_id || "");
  const school = modalSchools.find((item) => String(item.school_code) === String(modalSchool));
  const matchesSchool = (item) => String(item.ysb_school_id) === String(modalSchool) ||
    (school?.id != null && String(item.ysb_school_id) === String(school.id));
  // Penting: ID tingkatan sering berbeda antar sekolah, walaupun namanya sama.
  // Ambil tingkatan asal dari SEKOLAH terpilih, bukan dari seluruh data satu cabang.
  const schoolLevels = modalLevels.filter((item) =>
    matchesSchool(item) && String(item.ysb_branch_id) === String(modalBranch)
  ).sort((a, b) => studentNameCollator.compare(String(a.name_level || ""), String(b.name_level || "")));
  const selectedLevel = schoolLevels.find((item) => String(item.id) === String(destinationLevelId));
  const sourceLevelId = mode === "semester" ? destinationLevelId :
    schoolLevels.find((item) => Number(item.name_level) === Number(selectedLevel?.name_level) - 1)?.id;
  const sourceLevel = schoolLevels.find((item) => String(item.id) === String(sourceLevelId));
  const sourceClasses = sortClassesByName(allClasses.filter((item) =>
    String(item.ysb_grade_level_id) === String(sourceLevelId) &&
    matchesSchool(item) && String(item.ysb_branch_id) === String(modalBranch)
  ));
  const destinationClasses = sortClassesByName(allClasses.filter((item) =>
    String(item.ysb_grade_level_id) === String(destinationLevelId) &&
    matchesSchool(item) && String(item.ysb_branch_id) === String(modalBranch)
  )).map((item) => ({ id: item.id, name_kelas: item.name_kelas, label: ` ${item.name_kelas}` }));
  const destinationStudents = useMemo(() => [
    ...(savedPlacements[destinationClass] || []),
    ...(placements[destinationClass] || []),
  ], [savedPlacements, placements, destinationClass]);

  // Semua opsi modal dimuat berdasarkan pilihan modal, bukan filter halaman Home.
  useEffect(() => {
    if (!props.show || !modalBranch) return;
    let active = true;
    const accessLevel = localStorage.getItem("level");
    const query = `page=1&limit=1000&ascending=1&branch=${encodeURIComponent(modalBranch)}&level=${encodeURIComponent(accessLevel || "")}`;
    setLoadingOptions(true);
    setModalSchools([]);
    setModalLevels([]);
    setAllClasses([]);
    Promise.all([
      APIMS.get(`/api/access/schools?${query}`, headers),
      APIMS.get(`/api/grade-levels?${query}`, headers),
      APIMS.get(`/api/kelas?${query}`, headers),
    ]).then(([schoolsResponse, levelsResponse, classesResponse]) => {
      if (!active) return;
      let schools = (schoolsResponse?.data?.data || []).filter((item) =>
        String(item.ysb_branch_id) === String(modalBranch)
      );
      if (modalBranch === "BKS") schools = schools.filter((item) => item.school_code !== "BASEMENT_BEKASI");
      schools.sort(compareSchools);
      setModalSchools(schools);
      setModalLevels(levelsResponse?.data?.data || []);
      setAllClasses(classesResponse?.data?.data || []);
      setModalSchool((current) => schools.some((item) => String(item.school_code) === String(current))
        ? current : (schools[0]?.school_code || ""));
    }).catch((error) => {
      if (active) swal("Gagal", error?.response?.data?.message || "Gagal memuat filter modal.", "error");
    }).finally(() => { if (active) setLoadingOptions(false); });
    return () => { active = false; };
  }, [props.show, modalBranch]);

  useEffect(() => {
    if (loadingOptions || !modalSchool) return;
    if (!schoolLevels.some((item) => String(item.id) === String(destinationLevelId))) {
      setDestinationLevelId(schoolLevels[0]?.id || "");
    }
  }, [modalBranch, modalSchool, modalLevels, loadingOptions]);

  useEffect(() => {
    const term = destinationTerm;
    const prev = semesters.filter((item) => semesterOrder(item) < semesterOrder(term)).at(-1);
    setSourceSemester(prev?.id || "");
  }, [destinationSemester, props.semesters]);

  useEffect(() => {
    // Jangan reset kelas pilihan Home saat opsi modal sedang dimuat ulang.
    // Contoh: Home memilih Kelas B, daftar kelas sempat [] sebelum API selesai;
    // jika langsung fallback, pilihan akan berubah menjadi Kelas A.
    if (loadingOptions || !modalBranch || !modalSchool || !destinationLevelId || !destinationClasses.length) return;

    // Pertahankan kelas dari Home (atau yang dipilih user di modal) bila masih valid.
    // Kelas Tujuan dan Kelas Berikutnya memakai state destinationClass yang sama.
    setDestinationClass((current) =>
      destinationClasses.some((item) => String(item.id) === String(current))
        ? current
        : destinationClasses[0].id
    );
  }, [loadingOptions, allClasses, mode, destinationLevelId, modalSchool, modalBranch]);
  useEffect(() => {
    if (!sourceClasses.some((item) => String(item.id) === String(sourceClass))) setSourceClass(sourceClasses[0]?.id || "");
  }, [allClasses, mode, destinationLevelId, modalSchool, modalBranch]);
  useEffect(() => {
    if (props.show) return;
    setSourceStudents([]);
    setPlacements({});
    setSavedPlacements({});
    setSelectedSource([]);
    setSelectedDestination([]);
  }, [props.show]);

  useEffect(() => {
    setSourceStudents([]);
    setSelectedSource([]);
    if (!props.show) return;
    if (loadingOptions || !modalBranch || !modalSchool || !sourceSemester || !sourceClass || !sourceLevelId || !destinationTerm?.id || !activeYear?.id || !destinationClasses.length) return;
    let active = true;
    const term = semesters.find((item) => item.id === sourceSemester);
    setLoadingSource(true);
    const sourcePayload = {
      ysb_academic_year_id: academicYearId(term, props.academicYears || []), ysb_semester_id: sourceSemester,
      ysb_branch_id: modalBranch, ysb_school_id: modalSchool,
      ysb_grade_level_id: sourceLevelId, ysb_kelas_id: sourceClass,
    };
    const destinationPayload = {
      ysb_academic_year_id: activeYear.id, ysb_semester_id: destinationTerm.id,
      ysb_branch_id: modalBranch, ysb_school_id: modalSchool,
      ysb_grade_level_id: destinationLevelId,
    };
    Promise.all([
      APISD.post("/api/student-rombels", sourcePayload, headers),
      ...destinationClasses.map((item) => APISD.post("/api/student-rombels", {
        ...destinationPayload, ysb_kelas_id: item.id,
      }, headers)),
    ]).then(([sourceResponse, ...destinationResponses]) => {
      if (!active) return;
      setSavedPlacements(Object.fromEntries(destinationClasses.map((classItem, index) => [
        classItem.id,
        (destinationResponses[index]?.data?.data?.students || []).map((item) => ({
          id: item.ysb_student_id, nisn: item.nisn, name: item.nama_siswa, saved: true,
        })).filter((item) => item.id),
      ])));
      const assignedIds = new Set(destinationResponses.flatMap((response) =>
        response?.data?.data?.students || []
      ).map((item) => String(item.ysb_student_id)));
      // Jangan tampilkan lagi siswa yang sedang dipindahkan (belum disimpan),
      // walaupun kelas asal diganti dan data API dimuat ulang.
      const pendingIds = new Set(Object.values(placementsRef.current)
        .flatMap((students) => students.map((student) => String(student.id))));
      setSourceStudents((sourceResponse?.data?.data?.students || []).map((item) => ({
        id: item.ysb_student_id, nisn: item.nisn, name: item.nama_siswa,
      })).filter((item) => item.id && !assignedIds.has(String(item.id)) && !pendingIds.has(String(item.id))));
    }).catch((error) => { if (active) swal("Gagal", error?.response?.data?.message || "Gagal memuat siswa kelas asal.", "error"); })
      .finally(() => { if (active) setLoadingSource(false); });
    return () => { active = false; };
  }, [props.show, loadingOptions, sourceSemester, sourceClass, sourceLevelId, modalBranch, modalSchool,
    destinationLevelId, destinationTerm?.id, activeYear?.id,
    destinationClasses.map((item) => item.id).join(",")]);

  const filteredSource = useMemo(() => {
    const keyword = normalize(sourceSearch);
    return sortStudentsByName(sourceStudents.filter(
      (student) =>
        !keyword || normalize(student.name).includes(keyword) ||
        normalize(student.nisn).includes(keyword)
    ));
  }, [sourceSearch, sourceStudents]);

  const filteredDestination = useMemo(() => {
    const keyword = normalize(destinationSearch);
    return sortStudentsByName(destinationStudents.filter(
      (student) =>
        !keyword || normalize(student.name).includes(keyword) ||
        normalize(student.nisn).includes(keyword)
    ));
  }, [destinationSearch, destinationStudents]);

  const totalPlaced = Object.values(placements).reduce(
    (total, students) => total + students.length,
    0
  );

  const toggleSelected = (id, selected, setter) => {
    setter((previous) =>
      previous.includes(id)
        ? previous.filter((studentId) => studentId !== id)
        : [...previous, id]
    );
  };

  const toggleAllSource = () => {
    const visibleIds = filteredSource.map((student) => student.id);
    const allSelected =
      visibleIds.length > 0 &&
      visibleIds.every((id) => selectedSource.includes(id));

    setSelectedSource((previous) =>
      allSelected
        ? previous.filter((id) => !visibleIds.includes(id))
        : [...new Set([...previous, ...visibleIds])]
    );
  };

  const toggleAllDestination = () => {
    const visibleIds = filteredDestination.filter((student) => !student.saved).map((student) => student.id);
    const allSelected =
      visibleIds.length > 0 &&
      visibleIds.every((id) => selectedDestination.includes(id));

    setSelectedDestination((previous) =>
      allSelected
        ? previous.filter((id) => !visibleIds.includes(id))
        : [...new Set([...previous, ...visibleIds])]
    );
  };

  const moveToDestination = () => {
    if (!destinationClass || loadingSource) return;
    if (selectedSource.length === 0) {
      swal("Pilih siswa", "Pilih minimal satu siswa yang akan dipindahkan.", "warning");
      return;
    }

    const movingStudents = sourceStudents.filter((student) =>
      selectedSource.includes(student.id)
    ).map((student) => ({ ...student, originClass: sourceClass }));

    setPlacements((previous) => ({
      ...previous,
      [destinationClass]: [...(previous[destinationClass] || []), ...movingStudents].sort(
        (a, b) => a.name.localeCompare(b.name, "id")
      ),
    }));
    setSourceStudents((previous) =>
      previous.filter((student) => !selectedSource.includes(student.id))
    );
    setSelectedSource([]);
  };

  const returnToSource = () => {
    if (selectedDestination.length === 0) {
      swal("Pilih siswa", "Pilih minimal satu siswa yang akan dikembalikan.", "warning");
      return;
    }

    const returningStudents = destinationStudents.filter((student) =>
      !student.saved && selectedDestination.includes(student.id)
    );

    // Hanya kembalikan ke daftar kiri jika siswa berasal dari kelas yang sedang dipilih.
    // Untuk kelas asal lain, siswa muncul kembali saat kelas tersebut dipilih.
    setSourceStudents((previous) => sortStudentsByName([
      ...previous,
      ...returningStudents.filter((student) => String(student.originClass) === String(sourceClass)),
    ]));
    setPlacements((previous) => ({
      ...previous,
      [destinationClass]: (previous[destinationClass] || []).filter(
        (student) => student.saved || !selectedDestination.includes(student.id)
      ),
    }));
    setSelectedDestination([]);
  };

  const resetModalPlacement = () => {
    setSourceClass("");
    setDestinationClass("");
    setSourceStudents([]);
    setPlacements({});
    setSavedPlacements({});
    setSelectedSource([]);
    setSelectedDestination([]);
    setSourceSearch("");
    setDestinationSearch("");
  };

  const handleModalBranch = (event) => {
    setModalBranch(event.target.value);
    setModalSchool("");
    setDestinationLevelId("");
    resetModalPlacement();
  };

  const handleModalSchool = (event) => {
    setModalSchool(event.target.value);
    setDestinationLevelId("");
    resetModalPlacement();
  };

  const handleDestinationLevel = (event) => {
    setDestinationLevelId(event.target.value);
    resetModalPlacement();
  };

  const handleDestinationClass = (event) => {
    setDestinationClass(event.target.value);
    setSelectedDestination([]);
    setDestinationSearch("");
  };

  const handleSourceClass = (event) => {
    const nextClass = event.target.value;
    setSourceClass(nextClass);
    setSelectedSource([]);
    setSelectedDestination([]);

    if (mode === "semester" && destinationClasses.some((item) => String(item.id) === String(nextClass))) {
      setDestinationClass(nextClass);
      setSelectedDestination([]);
      setDestinationSearch("");
    }
  };

  const handleSave = async () => {
    if (loadingSource || loadingOptions || saving || !totalPlaced || !destinationClass || !destinationSemester) return;
    setSaving(true);
    try {
      for (const [classId, students] of Object.entries(placements)) {
        if (!students.length) continue;
        const response = await APISD.post("/api/student-rombels/store", {
          ysb_academic_year_id: activeYear?.id, 
          ysb_semester_id: destinationTerm?.id,
          ysb_branch_id: modalBranch, 
          ysb_school_id: modalSchool,
          ysb_grade_level_id: destinationLevelId, 
          ysb_kelas_id: classId,
          student: students.map((item) => item.id), user_id: userId,
        }, headers);
        if (response?.data?.error === true) throw new Error(response.data.message);
        setSavedPlacements((previous) => ({ ...previous, [classId]: [
          ...(previous[classId] || []), ...students.map((student) => ({ ...student, saved: true })),
        ] }));
        setPlacements((previous) => ({ ...previous, [classId]: [] }));
      }
      await props.onSaved?.();
      // swal("Berhasil", `${totalPlaced} siswa berhasil ditempatkan.`, "success");
      ToastSuccess.fire({
          icon: 'success',
          title:  `${totalPlaced} siswa berhasil ditempatkan.`,
      })
      props.onHide();
    } catch (error) {
      // swal("Gagal", error?.response?.data?.message || error.message || "Gagal menyimpan rombel.", "error");
      ToastError.fire({
        icon: "error",
        title: error.response?.data?.message || "Gagal menyimpan data",
      });
    } finally { setSaving(false); }
  };

  return (
    <Modal
      {...props}
      centered
      backdrop="static"
      dialogClassName="promotion-modal-dialog"
      contentClassName="module-modal-content promotion-modal-content"
      aria-labelledby="promotion-modal-title"
      size="xl"
    >
      <Modal.Header className="module-modal-header">
        <div>
          <h5 id="promotion-modal-title" className="module-modal-title">
            {config.title}
          </h5>
          <p className="module-modal-subtitle mb-4">
            {config.subtitle}
          </p>
        </div>

        <button
          type="button"
          className="module-modal-close"
          onClick={props.onHide}
          aria-label="Tutup modal"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </Modal.Header>

      <Modal.Body className="module-modal-body promotion-modal-body">
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "16px", padding: "16px", marginBottom: "20px", background: "#eef6ff", border: "1px solid #d9e8fb", borderRadius: "12px" }}>
          <div>
            <span className="module-form-label">Semester Aktif</span>
            <div style={{ fontWeight: 600, color: "#3f5bea" }}>
              {destinationTerm ? `${destinationTerm.name_year} — Semester ${destinationTerm.semester}` : "Belum ada semester aktif"}
            </div>
          </div>
          <div className="promotion-mode-field">
            <span className="promotion-mode-label">Mode Pemindahan</span>
            <div className="promotion-mode-toggle">
              <button type="button" className="active" disabled>
                {mode === "semester" ? "Lanjut Semester" : "Kenaikan Kelas"}
              </button>
            </div>
          </div>

        </div>
        <div className="promotion-global-filter" style={{ display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "flex-end" }}>
          <div className="module-form-group" style={{ flex: "1 1 180px", minWidth: 0 }}>
            <label className="module-form-label">Cabang</label>
            <select className="module-form-select" value={modalBranch} onChange={handleModalBranch} disabled={saving} >
              {(props.branches || []).map((item) => <option key={item.branch_code} value={item.branch_code}>{item.branch_name}</option>)}
            </select>
          </div>
          <div className="module-form-group" style={{ flex: "1 1 180px", minWidth: 0 }}>
            <label className="module-form-label">Sekolah</label>
            <select className="module-form-select" value={modalSchool} onChange={handleModalSchool} disabled={saving || loadingOptions || !modalBranch} >
              <option value="">{loadingOptions ? "Memuat sekolah..." : "Pilih Sekolah"}</option>
              {modalSchools.map((item) => <option key={item.school_code} value={item.school_code}>{item.school_name}</option>)}
            </select>
          </div>
          <div className="module-form-group" style={{ flex: "1 1 180px", minWidth: 0 }}>
            <label className="module-form-label">Tingkatan</label>
            <select className="module-form-select" value={destinationLevelId} onChange={handleDestinationLevel} disabled={saving || loadingOptions || !modalSchool}>
              <option value="">Pilih Tingkatan</option>
              {schoolLevels.map((item) => <option key={item.id} value={item.id}>{item.name_level}</option>)}
            </select>
          </div>
          <div className="module-form-group" style={{ flex: "1 1 180px", minWidth: 0 }}>
            <label className="module-form-label">Kelas Tujuan</label>
            <select className="module-form-select" value={destinationClass} onChange={handleDestinationClass} disabled={saving || loadingOptions || !destinationLevelId || !destinationClasses.length}>
              <option value="">Pilih Kelas</option>
              {destinationClasses.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
            </select>
          </div>

        </div>

        {/* <div className={`promotion-mode-note ${mode}`}>
          <strong>{mode === "semester" ? "Lanjut Semester" : "Kenaikan Kelas"}</strong>
          <span>
            {mode === "semester"
              ? "Tahun ajaran dan tingkat tetap. Tujuan otomatis Semester 2 dengan rombel yang sama, tetapi masih dapat diganti."
              : "Tujuan otomatis berpindah ke tahun ajaran, Semester 1, dan tingkat berikutnya. Siswa dapat dibagi ke beberapa rombel."}
          </span>
        </div> */}

        <div className="promotion-workspace">
          <section className="promotion-panel">
            <div className="promotion-panel-head">
              <div>
                <span className="promotion-step">1</span>
                <strong>Kelas Asal</strong>
              </div>
              <span className="promotion-count">{sourceStudents.length} siswa</span>
            </div>

            <div className="promotion-filter-grid">
              <div className="module-form-group">
                <label className="module-form-label">Tahun Ajaran / Semester</label>
                <select
                  className="module-form-select"
                  value={sourceSemester}
                  disabled
                >
                  {previousTerm && <option value={previousTerm.id}>{previousTerm.name_year} (Semester {previousTerm.semester})</option>}
                </select>
              </div>
              <div className="module-form-group">
                <label className="module-form-label">Kelas Asal</label>
                <select
                  className="module-form-select"
                  value={sourceClass}
                  onChange={handleSourceClass}
                >
                  {!sourceClasses.length && <option value="">{loadingOptions ? "Memuat kelas asal..." : "Kelas asal tidak tersedia"}</option>}
                  {sourceClasses.map((item) => (
                    <option key={item.id} value={item.id}>{formatClassName(sourceLevel?.name_level, item.name_kelas)}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="promotion-search">
              <FontAwesomeIcon icon={faMagnifyingGlass} />
              <input
                value={sourceSearch}
                onChange={(event) => setSourceSearch(event.target.value)}
                placeholder="Cari nama siswa atau NISN..."
              />
            </div>

            <div className="promotion-table-wrap">
              <table className="promotion-table">
                <thead>
                  <tr>
                    <th className="promotion-check-cell">
                      <input
                        type="checkbox"
                        checked={
                          filteredSource.length > 0 &&
                          filteredSource.every((student) =>
                            selectedSource.includes(student.id)
                          )
                        }
                        onChange={toggleAllSource}
                      />
                    </th>
                    <th>NISN</th>
                    <th>Nama Siswa</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSource.length === 0 ? (
                    <tr><td colSpan="3" className="promotion-empty">{loadingSource ? "Memuat siswa..." : "Tidak ada siswa di kelas asal"}</td></tr>
                  ) : (
                    filteredSource.map((student) => (
                      <tr key={student.id}>
                        <td className="promotion-check-cell">
                          <input
                            type="checkbox"
                            checked={selectedSource.includes(student.id)}
                            onChange={() =>
                              toggleSelected(student.id, selectedSource, setSelectedSource)
                            }
                          />
                        </td>
                        <td>{student.nisn}</td>
                        <td className="promotion-student-name">{student.name}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <div className="promotion-actions">
            <button
              type="button"
              className="promotion-move-button primary"
              onClick={moveToDestination}
              disabled={selectedSource.length === 0 || !destinationClass || loadingSource}
              title="Pindahkan ke kelas tujuan"
            >
              <span>{selectedSource.length || ""}</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
            <button
              type="button"
              className="promotion-move-button"
              onClick={returnToSource}
              disabled={selectedDestination.length === 0}
              title="Kembalikan ke kelas asal"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              <span>{selectedDestination.length || ""}</span>
            </button>
          </div>

          <section className="promotion-panel destination">
            <div className="promotion-panel-head">
              <div>
                <span className="promotion-step">2</span>
                <strong>Kelas Tujuan</strong>
              </div>
              <span className="promotion-count">{destinationStudents.length} siswa</span>
            </div>

            <div className="promotion-filter-grid">
              <div className="module-form-group">
                <label className="module-form-label">Tahun Ajaran / Semester</label>
                <select
                  className="module-form-select"
                  value={destinationTerm?.id || ""}
                  disabled
                >
                  {destinationTerm && <option value={destinationTerm.id}>{destinationTerm.name_year} (Semester {destinationTerm.semester})</option>}
                </select>
              </div>
              <div className="module-form-group">
                <label className="module-form-label">
                  {mode === "semester" ? "Kelas Semester Berikutnya" : "Kelas Berikutnya"}
                </label>
                <select
                  className="module-form-select"
                  value={destinationClass}
                  onChange={handleDestinationClass}
                >
                  {destinationClasses.map((classItem) => (
                    <option key={classItem.id} value={classItem.id}>
                      {formatClassName(selectedLevel?.name_level, classItem.name_kelas)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="promotion-search">
              <FontAwesomeIcon icon={faMagnifyingGlass} />
              <input
                value={destinationSearch}
                onChange={(event) => setDestinationSearch(event.target.value)}
                placeholder="Cari siswa yang sudah dipindahkan..."
              />
            </div>

            <div className="promotion-table-wrap">
              <table className="promotion-table">
                <thead>
                  <tr>
                    <th className="promotion-check-cell">
                      <input
                        type="checkbox"
                        checked={
                          filteredDestination.some((student) => !student.saved) &&
                          filteredDestination.filter((student) => !student.saved).every((student) =>
                            selectedDestination.includes(student.id)
                          )
                        }
                        disabled={loadingSource || saving || !filteredDestination.some((student) => !student.saved)}
                        onChange={toggleAllDestination}
                      />
                    </th>
                    <th>NISN</th>
                    <th>Nama Siswa</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDestination.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="promotion-empty">
                        Belum ada siswa di {destinationClasses.find((item) => String(item.id) === String(destinationClass))?.label}
                      </td>
                    </tr>
                  ) : (
                    filteredDestination.map((student) => (
                      <tr key={student.id} aria-disabled={student.saved || undefined} style={student.saved ? { background: "#f1f5f9", color: "#64748b" } : undefined}>
                        <td className="promotion-check-cell">
                          <input
                            type="checkbox"
                            disabled={student.saved || saving || loadingSource}
                            checked={!student.saved && selectedDestination.includes(student.id)}
                            onChange={() =>
                              toggleSelected(
                                student.id,
                                selectedDestination,
                                setSelectedDestination
                              )
                            }
                          />
                        </td>
                        <td>{student.nisn}</td>
                        <td className="promotion-student-name">{student.name}
                          {student.saved && <span style={{ display: "inline-block", marginLeft: "8px", padding: "2px 8px", borderRadius: "6px", fontSize: "11px", background: "#e2e8f0", color: "#475569" }}>Tersimpan</span>}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <div className="promotion-summary">
          <div>
            <span>Belum ditempatkan</span>
            <strong>{sourceStudents.length} siswa</strong>
          </div>
          {destinationClasses.map((classItem) => (
            <div key={classItem.id}>
              <span>{classItem.label}</span>
              <strong>{(savedPlacements[classItem.id] || []).length + (placements[classItem.id] || []).length} siswa</strong>
            </div>
          ))}
        </div>
      </Modal.Body>

      <Modal.Footer className="module-modal-footer promotion-modal-footer">
        <p>
          Perubahan belum tersimpan sampai tombol <strong>Simpan Perubahan</strong> ditekan.
        </p>
        <div>
          <button type="button" className="module-modal-cancel" onClick={props.onHide}>
            Batal
          </button>
          <button
            type="button"
            className="module-modal-submit"
            onClick={handleSave}
            disabled={totalPlaced === 0 || saving || loadingOptions || loadingSource || !activeYear?.id || !destinationTerm?.id || !userId}
          >
            Simpan Perubahan ({totalPlaced})
          </button>
        </div>
      </Modal.Footer>
    </Modal>

  );
}
